import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons/faBadgeCheck';
import { faOctagonXmark } from '@fortawesome/pro-solid-svg-icons/faOctagonXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, CardContent, Grid, Stack, Typography } from '@mui/material';
import { EnrollmentSummaryDto, EnrollStatus } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import { useModalUserProfile } from 'providers/UserProfileProvider';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditsResult } from 'views/lms/student/EnrollmentView/Shared/CreditsResult';
import { getEnrollmentCertificate } from 'views/lms/student/EnrollmentView/Shared/EnrollmentActions';
import { MprcCard, MprcContainer, MprcFormSubHeader, MprcLink, MprcText } from '../../../../../components';
import { ProgressResult } from './ProgressResult';
import { ScoreResult } from './ScoreResult';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    data: EnrollmentSummaryDto;
    mode: "student" | "instructor";
}

////////////////////////////////////////////////////////////////////////////

export const EnrollmentSummary: React.FC<IProps> = ({ data, mode }) => {

    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();

    return (
        <>
            <MprcContainer>
                <MprcCard paperColor={["paper", "dark"]} elevation={0}>
                    <CardContent>
                        <MprcFormSubHeader title={t("Student.EnrollmentView.Summary.EnrollmentInfoText")} margin="dense" />
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }} >
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="h6">{t("Student.EnrollmentView.Summary.EnrollDateText")}</Typography>
                                        <Typography variant="subtitle1" fontWeight="bold">{data.fullManaged ? Formaters.momentFullDateTime(data.enrolledOn, false, false) : "-"}</Typography>
                                    </Grid>
                                    {(mode == "instructor" || data.enrolledByManager) &&
                                        <Grid size={{ xs: 4 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <MprcLink onClick={() => showUser(data.enroller.userId)}>
                                                    <Avatar sx={{ width: 35, height: 35 }} src={data.enroller.thumbnailUrl}></Avatar>
                                                </MprcLink>
                                                <div>
                                                    <Typography variant="h6">{t("Student.EnrollmentView.Summary.EnrolledByText")}</Typography>
                                                    <Typography variant="subtitle1" fontWeight="bold">{data.enroller.name}</Typography>
                                                </div>
                                            </Stack>
                                        </Grid>
                                    }
                                    {mode == "instructor" &&
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="h6">{t("Student.EnrollmentView.Summary.IsMandatoryEnrollText")}</Typography>
                                            <Typography variant="subtitle1" fontWeight="bold" textTransform="uppercase">{data.isMandatoryEnroll ? t("shared:Literal.Yes") : t("shared:Literal.No")}</Typography>
                                        </Grid>
                                    }
                                    <Grid size={{ xs: 4 }}>
                                        {data.approvers?.map(item =>
                                            <Stack key={item.userId} direction="row" spacing={1} alignItems="center">
                                                <MprcLink onClick={() => showUser(item.userId)}>
                                                    <Avatar sx={{ width: 35, height: 35 }} src={item.thumbnailUrl}></Avatar>
                                                </MprcLink>
                                                <div>
                                                    <Typography variant="h6">{t("Student.EnrollmentView.Summary.ApproverText")}</Typography>
                                                    <Typography variant="subtitle1" fontWeight="bold">{item.name}</Typography>
                                                </div>
                                            </Stack>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Grid container alignItems="center" width="100%" spacing={2}>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="h6">{t("Student.EnrollmentView.Summary.EnrollPeriodText")}</Typography>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {(!data.startDate && !data.dueDate)
                                                ? data.fullManaged
                                                    ? t("Student.EnrollmentView.Summary.UndeterminedText")
                                                    : "-"
                                                : Formaters.performaDateRange(data.startDate, data.dueDate)
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="h6">{t("Student.EnrollmentView.Summary.FirstAccessText")}</Typography>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {data.fullManaged ?
                                                (data.firstAccess
                                                    ? Formaters.momentFullDateTime(data.firstAccess, false, false)
                                                    : t("Student.EnrollmentView.Summary.NeverAccessedText")
                                                )
                                                : "-"}
                                        </Typography>
                                    </Grid>
                                    {data.lastAccess &&
                                        <Grid size={{ xs: 4 }}>
                                            <Typography variant="h6">{t("Student.EnrollmentView.Summary.LastAccessText")}</Typography>
                                            <Typography variant="subtitle1" fontWeight="bold">{Formaters.momentFullDateTime(data.lastAccess, false, false)}</Typography>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </MprcCard>
            </MprcContainer>
            <Grid container columnSpacing={1} rowSpacing={4}>
                <Grid size={{ xs: 6 }}>
                    {data.progressByCredits ?
                        <CreditsResult
                            variant={"block"}
                            data={{
                                progressPercent: data.progressPercent,
                                credits: data.credits ?? 0,
                                enrollmentId: data.enrollmentId,
                                isClosed: data.finalizationDate != null,
                                maxCredits: data.maxCredits,
                                minCredits: data.minCredits,
                                status: data.status,
                                totalCompletedMandatoryComponents: data.totalCompletedMandatoryComponents,
                                totalMandatoryComponents: data.totalMandatoryComponents,
                            }}
                        />
                        :
                        <ProgressResult
                            variant={"block"}
                            data={{
                                enrollmentId: data.enrollmentId,
                                progress: data.progressPercent,
                                minProgress: data.minProgress ?? 0,
                                status: data.status,
                                isClosed: data.isFinalized,
                                totalComponents: data.progressTotalItems,
                                totalCompletedComponents: data.progressTotalCompletedItems,
                                totalCompletedMandatoryComponents: data.totalCompletedMandatoryComponents,
                                totalMandatoryComponents: data.totalMandatoryComponents
                            }}

                        />
                    }
                </Grid>
                {(mode == "instructor" && data.hasScore) &&
                    <Grid size={{ xs: 6 }}>
                        {data.scoreIsPercent ?
                            <ScoreResult
                                variant="multiple"
                                enrollmentId={data.enrollmentId}
                                score={data.scorePercent}
                                minScore={data.minScore}
                                isClosed={data.isFinalized}
                                totalItems={data.scoreTotalItems}
                                totalCompletedItems={data.scoreTotalCompletedItems}
                                status={data.status}
                            />
                            :
                            <>
                                <Typography variant="body2" component="span" textTransform="uppercase">
                                    {t("Student.EnrollmentView.FinalScoreText")}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" component="span" color={data.status == EnrollStatus.Approved ? "success" : "error"}>
                                    &nbsp;{data.scorePercent}
                                </Typography>
                            </>
                        }
                    </Grid>
                }
                {data.timeSpent &&
                    <Grid size={{ xs: 6 }}>
                        <Typography variant="subtitle1" textTransform="uppercase">
                            {t("Student.EnrollmentView.Summary.TimeSpentText")}
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {Formaters.timeSpanToTime(data.timeSpent)}
                        </Typography>
                    </Grid>
                }
            </Grid>

            {mode == "instructor" && (data.finalizationDate || data.conclusionFormReportedOn) &&
                <>
                    <Grid container spacing={2} sx={{ mt: 3 }}>
                        <Grid size={{ xs: 6 }}>
                            {data.conclusionFormReportedOn != null
                                ?
                                <>
                                    <Typography variant="subtitle1" textTransform="uppercase">
                                        {t("Student.EnrollmentView.Summary.ReportedOnText")}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="bold" color={data.status == EnrollStatus.Approved ? "success" : "error"}>
                                        {Formaters.momentFullDateTime(data.conclusionFormReportedOn, false, false)}
                                    </Typography>
                                </>
                                : data.finalizationDate != null &&
                                <>
                                    <Typography variant="subtitle1" textTransform="uppercase">
                                        {t("Student.EnrollmentView.Summary.FinalizedAtText")}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="bold" color={data.status == EnrollStatus.Approved ? "success" : "error"}>
                                        {Formaters.momentFullDateTime(data.finalizationDate, false, false)}
                                    </Typography>
                                </>
                            }
                            {
                                data.canReview &&
                                <Typography variant="caption" component="p" color="muted">
                                    {t("Student.EnrollmentView.Summary.CanReviewText")}
                                    {data.reviewUntil && <>&nbsp;{t("shared:Literal.Until")}&nbsp;{Formaters.momentFullDateTime(data.reviewUntil, false, false)}</>}
                                </Typography>
                            }
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <MprcText color={data.status == EnrollStatus.Reproved ? "error" : data.status == EnrollStatus.Approved ? "success" : "muted"}>
                                    <FontAwesomeIcon
                                        icon={data.status == EnrollStatus.Reproved ? faOctagonXmark : faBadgeCheck}
                                        fontSize={40}
                                        fixedWidth
                                    />
                                </MprcText>
                                <div>
                                    <Typography variant="h4" component="p" color={data.status == EnrollStatus.Reproved ? "error.main" : data.status == EnrollStatus.Approved ? "success.main" : "muted.main"}>
                                        {data.status == EnrollStatus.Approved
                                            ? t(data.hasScore ? "Student.EnrollmentView.ApprovedText" : "Student.EnrollmentView.CompletedText")
                                            : data.status == EnrollStatus.Reproved
                                                ? t(data.hasScore ? "Student.EnrollmentView.ReprovedText" : "Student.EnrollmentView.NotCompletedText")
                                                : t("Student.EnrollmentView.CertificateExpiredText")}
                                    </Typography>
                                    <Typography variant="caption" component="p">
                                        {data.status == EnrollStatus.Approved && data.certificateValidUntil && t("Student.EnrollmentView.CertificateValidUntilText", { date: Formaters.dayMonth(data.certificateValidUntil) })}
                                        {data.status == EnrollStatus.Reproved && data.reprovedByExpireDate && t("Student.EnrollmentView.DeadlineExpiredText", { date: Formaters.dayMonth(data.expireDate) })}
                                        {data.status == EnrollStatus.Lapsed && Formaters.dayMonth(data.certificateValidUntil)}
                                    </Typography>
                                    {data.certificateFileId &&
                                        <Typography variant="caption" component="p">
                                            <MprcLink fontSize="inherit" onClick={() => getEnrollmentCertificate(data.certificateFileId!)}>{t("Student.EnrollmentView.DownloadCertificateText")}</MprcLink>
                                        </Typography>
                                    }
                                </div>
                            </Stack>
                        </Grid>
                    </Grid>
                </>
            }
        </>
    );

};
